/**
 * 消息保存 Composable
 * 负责消息的数据库持久化操作（保存和加载）
 */

import { ChatRole } from "~/enums";
import { useMemorySettings } from "./useMemorySettings";

export function useMessageSave() {
  const { isMemoryEnabled } = useMemorySettings();

  /**
   * 保存消息到数据库
   * @param sessionId - 会话 ID
   * @param userId - 用户 ID
   * @param role - 消息角色
   * @param content - 消息内容
   * @param sortIndex - 消息排序索引
   * @param thinkingSteps - 思考步骤（可选）
   * @param attachments - 附件列表（可选）
   * @returns 保存的消息对象，失败返回 null
   */
  const saveMessage = async (
    sessionId: string,
    userId: string,
    role: string,
    content: string,
    sortIndex: number,
    thinkingSteps?: any[],
    attachments?: any[],
  ) => {
    try {
      // 🔗调用接口:保存消息到数据库
      const response = await $fetch("/api/message/messages", {
        method: "POST",
        body: {
          sessionId,
          userId,
          role,
          content,
          sortIndex,
          thinkingSteps,
          attachments,
          memoryEnabled: isMemoryEnabled(),
        },
      });

      // 后端直接返回消息对象，不是 { success, message } 格式
      if (response && response.id) {
        console.log("💾 消息已保存到数据库:", response.id);
        return response;
      }
    } catch (error) {
      console.error("❌ 保存消息失败:", error);
    }
    return null;
  };

  /**
   * 从数据库加载并转换消息
   * @param sessionId - 会话 ID
   * @param userId - 用户 ID
   * @returns 转换后的消息数组
   */
  const loadAndConvertMsg = async (sessionId: string, userId: string) => {
    try {
      // 🔗调用接口:获取会话消息
      const response: any = await $fetch("/api/message/messages", {
        method: "GET",
        query: { sessionId, userId },
      });

      if (response.success && response.messages) {
        return response.messages.map((msg: any) => ({
          id: msg.id,
          sessionId: msg.sessionId,
          role: msg.role,
          content: msg.content,
          parts: [{ type: "text", text: msg.content }],
          createdAt: new Date(msg.createdAt),
          thinkingSteps: msg.thinkingSteps || [],
          attachments: msg.attachments || [],
        }));
      }
    } catch (error) {
      console.error("❌ 加载消息失败:", error);
    }
    return [];
  };

  return {
    saveMessage,
    loadAndConvertMsg,
  };
}
